import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import MainLayout from "../Themes/MainLayout";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  CssBaseline,
  Box,
  Chip,
  Tabs,
  Tab,
  Pagination,
  MenuItem,
  InputAdornment,
  Tooltip,
  Drawer,
  Divider,
  IconButton,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import { useCurrentUser } from "../utils/useCurrentUser";
import { normalizeRole, APPROVAL_ROLES } from "../utils/normalizeRole";
import axios from "axios";
import { Autocomplete } from "@mui/material";

// Safe date formatter for date-only strings — no UTC shift, always treats value as IST calendar date
const fmtDate = (d) => d ? moment(String(d).slice(0, 10), "YYYY-MM-DD").format("MM/DD/YYYY") : "—";
// Safe formatter for full timestamps (submitted_at, actioned_on)
const fmtTs   = (d) => d ? moment(d).format("MM/DD/YYYY") : "—";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const apiBaseUrl = `${BASE_URL}/api/leave-request`;

const statusChip = (status) => {
  const s = (status || "").toLowerCase().trim();
  if (s === "approved")
    return <Chip label="Approved" size="small" sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }} />;
  if (s === "rejected")
    return <Chip label="Rejected" size="small" sx={{ bgcolor: "#ffebee", color: "#c62828", fontWeight: 600 }} />;
  if (s === "cancelled")
    return <Chip label="Cancelled" size="small" sx={{ bgcolor: "#f5f5f5", color: "#616161", fontWeight: 600 }} />;
  if (s === "cancellation rejected")
    return <Chip label="Cancellation Rejected" size="small" sx={{ bgcolor: "#ffebee", color: "#c62828", fontWeight: 600 }} />;
  if (s.startsWith("cancellation pending") || (s.startsWith("waiting for") && s.includes("cancellation")))
    return <Chip label="Cancellation Pending" size="small" sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600 }} />;
  // Dynamic: show exact status string for in-progress states
  if (s.startsWith("pending, "))
    return <Chip label={status} size="small" sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />;
  if (s === "waiting for admin approval")
    return <Chip label="Awaiting Admin" size="small" sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 }} />;
  if (s === "waiting for system admin approval")
    return <Chip label="Awaiting System Admin" size="small" sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 }} />;
  // "pending" = waiting for TL
  return <Chip label="Awaiting TL" size="small" sx={{ bgcolor: "#fff8e1", color: "#e65100", fontWeight: 600 }} />;
};

const DescriptionCell = ({ text, requestId, expandedId, setExpandedId }) => {
  if (!text) return <span style={{ color: "#aaa" }}>—</span>;
  const isLong = text.length > 40;
  const isExpanded = expandedId === requestId;
  return (
    <Box sx={{ fontSize: "0.78rem", lineHeight: 1.5, width: 140, overflow: "hidden" }}>
      {isLong ? (
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, flexWrap: "nowrap" }}>
          <Box sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flexShrink: 1,
            minWidth: 0,
          }}>
            {text.slice(0, 40)}…
          </Box>
          <span
            onClick={() => setExpandedId(isExpanded ? null : requestId)}
            style={{ color: "#1976d2", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, fontSize: "0.75rem" }}
          >
            {isExpanded ? "less" : "more"}
          </span>
        </Box>
      ) : (
        <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {text}
        </Box>
      )}
    </Box>
  );
};

const LeaveApproval = () => {
  const { empId, boId, roles } = useCurrentUser();

  // ── tab state ──
  const [activeTab, setActiveTab] = useState(0);

  // ── pending approvals state ──
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [userRole, setUserRole] = useState(null);
  const [projectResourcesMap, setProjectResourcesMap] = useState({});
  const [expandedDescId, setExpandedDescId] = useState(null);
  const [drawerRow, setDrawerRow] = useState(null);
  const [historyDrawerRow, setHistoryDrawerRow] = useState(null);

  // ── history state ──
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historySearch, setHistorySearch] = useState("");
  const [historyStatus, setHistoryStatus] = useState("");
  const [historyLeaveType, setHistoryLeaveType] = useState("");
  const [historyFromDate, setHistoryFromDate] = useState("");
  const [historyToDate, setHistoryToDate] = useState("");
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);
  const HISTORY_LIMIT = 10;

  // ── init: detect role ──
  useEffect(() => {
    console.log("[LeaveApproval] roles from useCurrentUser:", roles);
    if (!roles || (Array.isArray(roles) && roles.length === 0)) return;

    const rolesArray = Array.isArray(roles) ? roles : [roles];
    // Use highest-privilege role as primary for fetching approvals from backend
    const PRIORITY = ["System Admin", "Admin", "HR", "Project Manager", "Technical Lead"];
    const normalized = rolesArray.map(normalizeRole);
    console.log("[LeaveApproval] normalized roles:", normalized);
    const resolved = PRIORITY.find((p) => normalized.includes(p)) || null;
    console.log("[LeaveApproval] resolved userRole:", resolved);

    if (resolved) setUserRole(resolved);
    else setError("User has no approval role");

    axios.get(`${apiBaseUrl}/leave-types`)
      .then((res) => setLeaveTypeOptions(res.data.client_type || []))
      .catch(() => {});
  }, [roles]);

  useEffect(() => {
    if (userRole) fetchApprovals();
  }, [userRole]);

  useEffect(() => {
    if (userRole && activeTab === 1) fetchHistory();
  }, [userRole, activeTab, historyPage, historySearch, historyStatus, historyLeaveType, historyFromDate, historyToDate]);

  // ── pending approvals ──
  const fetchApprovals = async () => {
    setLoading(true);
    setError(null);
    try {
      // For multi-role users, fetch approvals for each role they hold and merge
      // This ensures a PM+Admin sees both "Pending, 1 of%" (PM) and "Waiting for Admin" (Admin) requests
      const APPROVAL_ROLE_PRIORITY = ["System Admin", "Admin", "HR", "Project Manager", "Technical Lead"];
      const userRoles = getAllUserRoles().filter(r => APPROVAL_ROLE_PRIORITY.includes(r));

      const allResults = await Promise.all(
        userRoles.map(role =>
          fetch(`${BASE_URL}/LeaveApproval/approvals/${encodeURIComponent(role)}/${empId}/${boId}`)
            .then(r => r.ok ? r.json() : [])
            .catch(() => [])
        )
      );

      // Merge and deduplicate by request_id
      const seen = new Set();
      const merged = [];
      for (const rows of allResults) {
        for (const row of rows) {
          if (!seen.has(row.request_id)) {
            seen.add(row.request_id);
            merged.push(row);
          }
        }
      }

      console.log("[LeaveApproval] merged approvals from all roles:", merged.length);
      const mapped = merged
        .filter((row) => {
          const s = (row.approval_status || row.status || "").toLowerCase();
          return s !== "approved" && s !== "rejected";
        })
        .map((row) => {
          // Authority is PROJECT-BASED. Trust the backend's per-project `can_act`
          // flag (computed from clientprojects.technicalleadid/projectmanagerid/hrid
          // for the requester's project) rather than re-deriving from the user's
          // GLOBAL roles. A user who holds the TL role globally but is not the
          // assigned TL on this project must NOT get an active Approve button.
          const canAct = row.can_act === true;
          // The acting role is the stage this request is at — but only meaningful
          // when the backend confirms the user can act on it.
          const actingRole = canAct ? getStageRole(row.approval_status) : null;
          console.log(`[LeaveApproval] row ${row.request_id} | status="${row.approval_status}" | can_act=${canAct} | actingRole=${actingRole}`);
          return {
            ...row,
            full_name: `${row.first_name || "Unknown"} ${row.last_name || "Unknown"}`.trim(),
            can_act: canAct,
            acting_role: actingRole,
          };
        })
        // Sort by submitted_at descending
        .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));

      setApprovals(mapped);
      mapped.forEach((row) => {
        fetchProjectResourcesForEmployee(row.employee_id);
        if (row.alternative_resource)
          fetchProjectResourcesForEmployee(row.alternative_resource);
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAllUserRoles = () => {
    const rolesArray = Array.isArray(roles) ? roles : [roles || ""];
    return [
      ...new Set([userRole, ...rolesArray.map(normalizeRole)].filter(Boolean)),
    ];
  };

  // Derive the approval role a request's CURRENT STATUS corresponds to.
  // This is purely a function of the workflow stage — it does NOT depend on the
  // logged-in user's roles. The backend has already confirmed (via can_act) that
  // the user is the assigned approver for that project before we use this.
  const getStageRole = (approval_status) => {
    const s = (approval_status || "").toLowerCase().trim();
    if (s === "pending" || s === "cancellation pending") return "Technical Lead";
    if (s.startsWith("pending, 1 of") || s.startsWith("cancellation pending, 1 of")) return "Project Manager";
    if (s.startsWith("pending, 2 of") || s.startsWith("cancellation pending, 2 of")) return "HR";
    if (s === "waiting for admin approval" || s === "waiting for admin cancellation approval") return "Admin";
    if (s === "waiting for system admin approval" || s === "waiting for system admin cancellation approval") return "System Admin";
    return userRole;
  };

  const getPendingLabel = (row) => {
    const s = (row.approval_status || "").toLowerCase();
    if (s === "pending") return "Waiting for TL approval";
    if (s.startsWith("pending, 1 of")) return "Waiting for PM approval";
    if (s.startsWith("pending, 2 of")) return "Waiting for HR approval";
    if (s === "waiting for admin approval") return "Waiting for Admin approval";
    if (s === "waiting for system admin approval") return "Waiting for System Admin approval";
    if (s === "cancellation pending") return "Cancellation waiting for TL approval";
    if (s.startsWith("cancellation pending, 1 of")) return "Cancellation waiting for PM approval";
    if (s.startsWith("cancellation pending, 2 of")) return "Cancellation waiting for HR approval";
    if (s === "waiting for admin cancellation approval") return "Cancellation waiting for Admin approval";
    if (s === "waiting for system admin cancellation approval") return "Cancellation waiting for System Admin approval";
    return "";
  };

  // ── history fetch ──
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({
        page: historyPage,
        limit: HISTORY_LIMIT,
        ...(historySearch    && { search:     historySearch }),
        ...(historyStatus    && { status:     historyStatus }),
        ...(historyLeaveType && { leave_type: historyLeaveType }),
        ...(historyFromDate  && { from_date:  historyFromDate }),
        ...(historyToDate    && { to_date:    historyToDate }),
      });

      const res = await fetch(
        `${BASE_URL}/LeaveApproval/history/${encodeURIComponent(userRole)}/${empId}/${boId}?${params.toString()}`
      );
      const json = res.ok ? await res.json() : { data: [], total: 0 };
      setHistory(json.data || []);
      setHistoryTotal(json.total || 0);
    } catch (err) {
      console.error("History fetch error:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchProjectResourcesForEmployee = useCallback(
    async (targetEmployeeId) => {
      try {
        if (!targetEmployeeId) return;
        const res = await axios.get(
          `${apiBaseUrl}/project-resources/${targetEmployeeId}`,
          {
            params: { bo_id: boId },
          },
        );
        setProjectResourcesMap((prev) => ({
          ...prev,
          [targetEmployeeId]: res.data || [],
        }));
      } catch {
        setProjectResourcesMap((prev) => ({ ...prev, [targetEmployeeId]: [] }));
      }
    },
    [boId],
  );

  const getNameFromEmployeeId = (row) => {
    const resources = projectResourcesMap[row.employee_id] || [];
    const match = resources.find(
      (item) => item.employee_id === row.alternative_resource,
    );
    return match
      ? `${match.first_name} ${match.last_name}`
      : "No alternative resource selected";
  };

  const handleRemarksChange = (index, remarks) => {
    setApprovals((prev) => {
      const updated = [...prev];
      updated[index].remarks = remarks;
      return updated;
    });
  };

  // ── approve / reject handlers ──
  const handleApprove = async (row) => {
    if (row.is_cancellation) {
      await submitCancellationApproval({ row, role: row.acting_role });
    } else {
      await submitApproval({ row, role: row.acting_role });
    }
  };

  const submitCancellationApproval = async ({ row, role }) => {
    try {
      const res = await fetch(`${BASE_URL}/LeaveApproval/approve-cancellation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_id: row.request_id,
          bo_id: row.bo_id,
          approver_id: empId,
          approved_by: empId,
          userRole: normalizeRole(role),
          remarks: row.remarks || "",
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Cancellation approval failed: ${res.status}`);
      }
      setAlert({ type: "success", msg: "Cancellation approved — leave has been cancelled!" });
      setTimeout(() => setAlert({ type: "", msg: "" }), 3000);
      fetchApprovals();
    } catch (err) {
      setAlert({ type: "error", msg: `Error approving cancellation: ${err.message}` });
    }
  };

  const submitApproval = async ({ row, role }) => {
    try {
      const res = await fetch(`${BASE_URL}/LeaveApproval/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_id: row.request_id,
          bo_id: row.bo_id,
          approver_id: empId,
          approved_by: empId,
          userRole: normalizeRole(role),
          remarks: row.remarks || "",
          alternative_resource: row.alternative_resource || null,
        }),
      });
      if (!res.ok) throw new Error(`Approval failed: ${res.status}`);
      setAlert({ type: "success", msg: "Leave approved!" });
      setTimeout(() => setAlert({ type: "", msg: "" }), 3000);
      fetchApprovals();
    } catch (err) {
      setAlert({ type: "error", msg: `Error approving leave: ${err.message}` });
    }
  };

  const handleReject = async (row) => {
    try {
      const res = await fetch(`${BASE_URL}/LeaveApproval/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_id: row.request_id,
          bo_id: row.bo_id,
          employee_id: row.employee_id,
          approval_status: "Rejected",
          remarks: row.remarks,
          rejected_by: empId,
          userRole: normalizeRole(row.acting_role || userRole),
        }),
      });
      if (!res.ok) throw new Error("Rejection failed");
      setAlert({ type: "success", msg: "Leave rejected!" });
      setTimeout(() => setAlert({ type: "", msg: "" }), 3000);
      fetchApprovals();
    } catch (err) {
      setAlert({ type: "error", msg: `Error rejecting leave: ${err.message}` });
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <MainLayout>
      {alert.msg && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.msg}
        </Alert>
      )}

      {/* ── Tabs ── */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Pending Approvals" />
          <Tab label="History" />
        </Tabs>
      </Box>

      {/* ══════════════════════════════════════
          TAB 0 — Pending Approvals
      ══════════════════════════════════════ */}
      {activeTab === 0 && (
        <>
          {approvals.length === 0 && !loading && (
            <Typography color="text.secondary">No leaves pending approval.</Typography>
          )}
          {approvals.length > 0 && (
            <ThemeProvider theme={Table_theme}>
              <CssBaseline />
              <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid #e0e0e0" }} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell><strong>Employee</strong></TableCell>
                      <TableCell><strong>Project</strong></TableCell>
                      <TableCell><strong>Leave Type</strong></TableCell>
                      <TableCell><strong>Start Date</strong></TableCell>
                      <TableCell><strong>End Date</strong></TableCell>
                      <TableCell><strong>Days</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Submitted At</strong></TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approvals.map((row, index) => (
                      <TableRow
                        key={`${row.request_id}-${index}`}
                        hover
                        onClick={() => setDrawerRow({ ...row, index })}
                        sx={{
                          cursor: "pointer",
                          borderLeft: row.can_act ? "3px solid #1976d2" : "3px solid transparent",
                          "&:hover": { bgcolor: "#f0f7ff" },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {row.full_name}
                          {row.is_cancellation && (
                            <Chip label="Cancellation" size="small" sx={{ ml: 1, bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600, fontSize: "0.65rem" }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>{row.proj_names}</TableCell>
                        <TableCell>{row.leave_type}</TableCell>
                        <TableCell>{row.start_date ? fmtDate(row.start_date) : "—"}</TableCell>
                        <TableCell>{row.end_date ? fmtDate(row.end_date) : "—"}</TableCell>
                        <TableCell>{row.total_days ?? "—"}</TableCell>
                        <TableCell>{statusChip(row.approval_status)}</TableCell>
                        <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                          {fmtTs(row.submitted_at)}
                        </TableCell>
                        <TableCell align="right" sx={{ pr: 1.5 }}>
                          <Tooltip title={row.can_act ? "Click to review & approve" : "Click to view details"} placement="left">
                            <Button
                              size="small"
                              variant={row.can_act ? "contained" : "text"}
                              color={row.can_act ? "primary" : "inherit"}
                              onClick={(e) => { e.stopPropagation(); setDrawerRow({ ...row, index }); }}
                              sx={{
                                fontSize: "0.72rem",
                                px: 1.5,
                                borderRadius: 2,
                                whiteSpace: "nowrap",
                                color: row.can_act ? undefined : "text.secondary",
                              }}
                            >
                              {row.can_act ? "Review →" : "View →"}
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </ThemeProvider>
          )}

          {/* ── Detail Drawer ── */}
          <Drawer
            anchor="right"
            open={!!drawerRow}
            onClose={() => setDrawerRow(null)}
            PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, top: "64px", height: "calc(100% - 64px)" } }}
          >
            {drawerRow && (() => {
              const row = drawerRow;
              const index = drawerRow.index;
              return (
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
                  {/* Header — fixed, never scrolls */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, px: 3, pt: 3, pb: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>{row.full_name}</Typography>
                      {row.is_cancellation && (
                        <Chip label="Cancellation Request" size="small" sx={{ mt: 0.5, bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600, fontSize: "0.65rem" }} />
                      )}
                      <Typography variant="caption" color="text.secondary">{row.proj_names}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {statusChip(row.approval_status)}
                      <IconButton size="small" onClick={() => setDrawerRow(null)}><CloseIcon fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                  <Divider sx={{ flexShrink: 0 }} />

                  {/* Scrollable content */}
                  <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>

                  {/* Info grid */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                    {[
                      { label: "Leave Type", value: row.leave_type },
                      { label: "Start Date", value: row.start_date ? fmtDate(row.start_date) : "—" },
                      { label: "End Date", value: row.end_date ? fmtDate(row.end_date) : "—" },
                      { label: "Total Days", value: row.total_days ?? "—" },
                      { label: "Total Hours", value: row.total_hours ?? "—" },
                      { label: "Submitted At", value: fmtTs(row.submitted_at) },
                    ].map(({ label, value }) => (
                      <Box key={label} sx={{ minWidth: "45%" }}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Description */}
                  {row.leave_description && (
                    <>
                      <Typography variant="caption" color="text.secondary">Leave Description</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 1.5, bgcolor: "#fafafa" }}>
                        <Typography sx={{ fontSize: "0.83rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {row.leave_description}
                        </Typography>
                      </Paper>
                    </>
                  )}

                  {/* Alternative Resource */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Alternative Resource
                    </Typography>
                    {userRole === "Technical Lead" ? (
                      <Autocomplete
                        size="small"
                        options={(projectResourcesMap[row.employee_id] || []).map((item) => ({
                          label: `${item.first_name} ${item.last_name}`,
                          value: item.employee_id,
                        }))}
                        value={
                          (projectResourcesMap[row.employee_id] || [])
                            .map((item) => ({ label: `${item.first_name} ${item.last_name}`, value: item.employee_id }))
                            .find((opt) => opt.value === row.alternative_resource) || null
                        }
                        onOpen={() => fetchProjectResourcesForEmployee(row.employee_id)}
                        onChange={(_, newValue) => {
                          const updated = { ...row, alternative_resource: newValue?.value || "", alternative_resource_name: newValue?.label || "" };
                          setApprovals((prev) => prev.map((r, i) => i === index ? updated : r));
                          setDrawerRow(updated);
                        }}
                        renderInput={(params) => <TextField {...params} placeholder="--Select--" size="small" />}
                      />
                    ) : (
                      <Typography sx={{ fontSize: "0.83rem", color: row.alternative_resource ? "inherit" : "text.secondary" }}>
                        {row.alternative_resource ? getNameFromEmployeeId(row) : "No alternative resource selected"}
                      </Typography>
                    )}
                  </Box>

                  {/* Remarks */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Remarks</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      value={row.remarks || ""}
                      onChange={(e) => {
                        const updated = { ...row, remarks: e.target.value };
                        handleRemarksChange(index, e.target.value);
                        setDrawerRow(updated);
                      }}
                      placeholder="Add remarks..."
                    />
                  </Box>

                  {/* Actions */}
                  </Box>{/* end scrollable content */}
                  <Box sx={{ flexShrink: 0, px: 3, pb: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    {!row.can_act && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, textAlign: "center" }}>
                        {getPendingLabel(row)}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon />}
                        disabled={!row.can_act}
                        onClick={() => { handleApprove(row); setDrawerRow(null); }}
                        sx={{ borderRadius: 2 }}
                      >
                        Approve
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        startIcon={<CloseIcon />}
                        disabled={!row.can_act}
                        onClick={() => { handleReject(row); setDrawerRow(null); }}
                        sx={{ borderRadius: 2 }}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })()}
          </Drawer>
        </>
      )}

      {/* ══════════════════════════════════════
          TAB 1 — History
      ══════════════════════════════════════ */}
      {activeTab === 1 && (
        <>
          {/* ── Filters ── */}
          <Box display="flex" justifyContent="flex-start" alignItems="center" mt={0.5} mb={2} gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search employee..."
              value={historySearch}
              onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
              sx={{
                width: 250,
                "& input": { fontSize: 12 },
                "& input::placeholder": { fontSize: 12 },
              }}
              InputProps={{
                sx: { height: 36, paddingRight: 0 },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={historyStatus}
              onChange={(e) => { setHistoryStatus(e.target.value); setHistoryPage(1); }}
              sx={{
                width: 160,
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiSelect-select": { display: "flex", alignItems: "center", fontSize: 12, padding: "0 14px" },
                "& .MuiInputLabel-root": { fontSize: "12px" },
              }}
              SelectProps={{
                MenuProps: { PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: "12px", minHeight: "32px" } } } },
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
              <MenuItem value="Cancellation Requested">Cancelled</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Leave Type"
              value={historyLeaveType}
              onChange={(e) => { setHistoryLeaveType(e.target.value); setHistoryPage(1); }}
              sx={{
                width: 160,
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiSelect-select": { display: "flex", alignItems: "center", fontSize: 12, padding: "0 14px" },
                "& .MuiInputLabel-root": { fontSize: "12px" },
              }}
              SelectProps={{
                MenuProps: { PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: "12px", minHeight: "32px" } } } },
              }}
            >
              <MenuItem value="">All</MenuItem>
              {leaveTypeOptions.map((opt) => (
                <MenuItem key={opt.variable_value} value={opt.variable_value}>
                  {opt.variable_value}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="date"
              label="From"
              value={historyFromDate}
              onChange={(e) => { setHistoryFromDate(e.target.value); setHistoryPage(1); }}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 160,
                "& input": {
                  fontSize: 12,
                  color: historyFromDate ? "#000" : "#9e9e9e"
                },
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiInputLabel-root": { fontSize: "14px" },
              }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={historyToDate}
              onChange={(e) => { setHistoryToDate(e.target.value); setHistoryPage(1); }}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 160,
                "& input": {
                  fontSize: 12,
                  color: historyFromDate ? "#000" : "#9e9e9e"
                },
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiInputLabel-root": { fontSize: "12px" },
              }}
            />
            {(historySearch || historyStatus || historyLeaveType || historyFromDate || historyToDate) && (
              <Button
                size="small"
                variant="outlined"
                sx={{ height: 36, fontSize: 12, borderRadius: "8px" }}
                onClick={() => { setHistorySearch(""); setHistoryStatus(""); setHistoryLeaveType(""); setHistoryFromDate(""); setHistoryToDate(""); setHistoryPage(1); }}
              >
                Clear
              </Button>
            )}
          </Box>

          {/* ── History Table ── */}
          {historyLoading ? (
            <CircularProgress size={28} />
          ) : history.length === 0 ? (
            <Typography color="text.secondary">No history found.</Typography>
          ) : (
            <ThemeProvider theme={Table_theme}>
              <CssBaseline />
              <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid #e0e0e0" }} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell><strong>Employee</strong></TableCell>
                      <TableCell><strong>Leave Type</strong></TableCell>
                      <TableCell><strong>Start Date</strong></TableCell>
                      <TableCell><strong>End Date</strong></TableCell>
                      <TableCell><strong>Days</strong></TableCell>
                      <TableCell><strong>Submitted At</strong></TableCell>
                      <TableCell><strong>Final Status</strong></TableCell>
                      <TableCell><strong>Approval Chain</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((row, i) => {
                      const steps = Array.isArray(row.approval_steps) ? row.approval_steps : [];
                      const levelLabel = (lvl) =>
                        lvl === 1 ? "TL" : lvl === 2 ? "PM" : lvl === 3 ? "HR" : lvl === 4 ? "Admin" : "Sys Admin";
                      return (
                        <TableRow
                          key={`${row.request_id}-${i}`}
                          hover
                          onClick={() => setHistoryDrawerRow(row)}
                          sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f0f7ff" } }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            {`${row.first_name || ""} ${row.last_name || ""}`.trim()}
                          </TableCell>
                          <TableCell>{row.leave_type}</TableCell>
                          <TableCell>{row.start_date ? fmtDate(row.start_date) : "—"}</TableCell>
                          <TableCell>{row.end_date ? fmtDate(row.end_date) : "—"}</TableCell>
                          <TableCell>{row.total_days ?? "—"}</TableCell>
                          <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                            {row.submitted_at ? fmtTs(row.submitted_at) : "—"}
                          </TableCell>
                          <TableCell>{statusChip(row.current_status)}</TableCell>
                          <TableCell>
                            {/* Inline mini approval chain */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap" }}>
                              {steps.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              ) : steps.map((s, si) => (
                                <React.Fragment key={si}>
                                  {si > 0 && <Typography variant="caption" color="text.secondary">→</Typography>}
                                  <Chip
                                    label={`${levelLabel(s.level)}: ${s.action}`}
                                    size="small"
                                    sx={{
                                      fontSize: "0.68rem",
                                      height: 20,
                                      bgcolor: s.action === "Rejected" ? "#ffebee" : "#e8f5e9",
                                      color:   s.action === "Rejected" ? "#c62828"  : "#2e7d32",
                                    }}
                                  />
                                </React.Fragment>
                              ))}
                              {steps.length > 0 && (
                                <Typography variant="caption" color="primary" sx={{ cursor: "pointer", ml: 0.5, whiteSpace: "nowrap" }}>
                                  details →
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* ── Pagination ── */}
              {historyTotal > HISTORY_LIMIT && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Pagination
                    count={Math.ceil(historyTotal / HISTORY_LIMIT)}
                    page={historyPage}
                    onChange={(_, p) => setHistoryPage(p)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </ThemeProvider>
          )}

          {/* ── History Detail Drawer ── */}
          <Drawer
            anchor="right"
            open={!!historyDrawerRow}
            onClose={() => setHistoryDrawerRow(null)}
            PaperProps={{ sx: { width: { xs: "100%", sm: 440 }, p: 3 } }}
          >
            {historyDrawerRow && (() => {
              const row = historyDrawerRow;
              const steps = Array.isArray(row.approval_steps) ? row.approval_steps : [];
              const levelLabel = (lvl) =>
                lvl === 1 ? "Technical Lead" : lvl === 2 ? "Project Manager" : lvl === 3 ? "HR" : lvl === 4 ? "Admin" : "System Admin";
              return (
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  {/* Header */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                        {`${row.first_name || ""} ${row.last_name || ""}`.trim()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{row.leave_type}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {statusChip(row.current_status)}
                      <IconButton size="small" onClick={() => setHistoryDrawerRow(null)}><CloseIcon fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {/* Leave info */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                    {[
                      { label: "Start Date",    value: row.start_date    ? fmtDate(row.start_date)   : "—" },
                      { label: "End Date",      value: row.end_date      ? fmtDate(row.end_date)     : "—" },
                      { label: "Total Days",    value: row.total_days    ?? "—" },
                      { label: "Total Hours",   value: row.total_hours   ?? "—" },
                      { label: "Submitted At",  value: row.submitted_at  ? fmtTs(row.submitted_at)   : "—" },
                      { label: "Levels Required", value: row.approval_level_required ?? "—" },
                    ].map(({ label, value }) => (
                      <Box key={label} sx={{ minWidth: "45%" }}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Description */}
                  {row.leave_description && (
                    <>
                      <Typography variant="caption" color="text.secondary">Leave Description</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 1.5, bgcolor: "#fafafa" }}>
                        <Typography sx={{ fontSize: "0.83rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {row.leave_description}
                        </Typography>
                      </Paper>
                    </>
                  )}

                  {/* Approval timeline */}
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Approval Timeline
                  </Typography>
                  {steps.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">No approval actions recorded yet.</Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {steps.map((s, si) => (
                        <Paper
                          key={si}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            borderLeft: `4px solid ${s.action === "Rejected" ? "#ef5350" : "#66bb6a"}`,
                            bgcolor: s.action === "Rejected" ? "#fff8f8" : "#f9fff9",
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                              Step {s.step} — {levelLabel(s.level)}
                            </Typography>
                            <Chip
                              label={s.action}
                              size="small"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                bgcolor: s.action === "Rejected" ? "#ffebee" : "#e8f5e9",
                                color:   s.action === "Rejected" ? "#c62828"  : "#2e7d32",
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {s.approver_name} · {s.actioned_on ? fmtTs(s.actioned_on) : "—"}
                          </Typography>
                          {s.remarks && (
                            <Typography sx={{ fontSize: "0.78rem", mt: 0.5, color: "text.secondary", fontStyle: "italic" }}>
                              "{s.remarks}"
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Drawer>
        </>
      )}
    </MainLayout>
  );
};

export default LeaveApproval;